import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCalendars } from '@/api/calendar';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import { TextBookingWorkflow } from './TextBookingWorkflow';
import { BookingRequestWorkflow } from './BookingRequestWorkflow';
import { CalendarWorkflowComponent } from './CalendarWorkflowComponent';
import { IExistingBookingWorkflowsProps, IWorkflow } from '@/interfaces/ICalendarWorkflows';
import { EWorkflowViewFilter } from 'src/enums/ECalendarWorkflows';

export const ExistingBookingWorkflows: React.FC<IExistingBookingWorkflowsProps> = ({
	clientObject,
	onEditTextBooking,
	onEditBookingRequest,
	onDeleteTextBooking,
	onDeleteBookingRequest,
	viewFilter = EWorkflowViewFilter.ALL,
	onRefetch,
}) => {
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const { data: calendarData, refetch } = useQuery({
		queryKey: ['get-calendar', selectedAgentId],
		queryFn: () => (selectedAgentId ? getCalendars(selectedAgentId) : Promise.resolve(null)),
		enabled: !!selectedAgentId,
	});

	const getTextBookingWorkflows = (): IWorkflow[] => {
		if (!clientObject?.workflows) {
			return [];
		}
		try {
			const textWorkflows = JSON.parse(clientObject.workflows) || [];
			return textWorkflows.filter((w: IWorkflow) => w.bookingText === true);
		} catch {
			return [];
		}
	};

	const getBookingRequestWorkflows = (): IWorkflow[] => {
		if (!clientObject?.intake_questions_workflows) {
			return [];
		}
		try {
			const intakeWorkflows = JSON.parse(clientObject.intake_questions_workflows) || [];
			return intakeWorkflows.filter((w: IWorkflow) => w.bookingRequest === true);
		} catch {
			return [];
		}
	};

	const textBookingWorkflows = getTextBookingWorkflows();
	const bookingRequestWorkflows = getBookingRequestWorkflows();
	const calendarWorkflows = calendarData?.data?.calendars || [];

	const shouldShowTextBooking =
		viewFilter === EWorkflowViewFilter.ALL || viewFilter === EWorkflowViewFilter.TEXT_BOOKING;
	const shouldShowBookingRequest =
		viewFilter === EWorkflowViewFilter.ALL || viewFilter === EWorkflowViewFilter.BOOKING_REQUEST;
	const shouldShowCalendarWorkflow = viewFilter === EWorkflowViewFilter.ALL;

	const hasTextBookings = textBookingWorkflows.length > 0 && shouldShowTextBooking;
	const hasBookingRequests = bookingRequestWorkflows.length > 0 && shouldShowBookingRequest;
	const hasCalendarWorkflows = calendarWorkflows.length > 0 && shouldShowCalendarWorkflow;

	if (!hasTextBookings && !hasBookingRequests && !hasCalendarWorkflows) {
		return null;
	}

	let workflowCounter = 1;

	const handleRefetch = () => {
		refetch();
		onRefetch?.();
	};

	return (
		<div className="space-y-6">
			<div className="max-h-[600px] overflow-y-auto space-y-4 pr-2">
				{hasTextBookings &&
					textBookingWorkflows.map((workflow: IWorkflow, index: number) => (
						<TextBookingWorkflow
							key={workflow.id || index}
							workflow={workflow}
							workflowNumber={workflowCounter++}
							onEdit={onEditTextBooking}
							onDelete={onDeleteTextBooking}
						/>
					))}

				{hasBookingRequests &&
					bookingRequestWorkflows.map((workflow: IWorkflow, index: number) => (
						<BookingRequestWorkflow
							key={workflow.id || index}
							workflow={workflow}
							workflowNumber={workflowCounter++}
							onEdit={onEditBookingRequest}
							onDelete={onDeleteBookingRequest}
						/>
					))}

				{hasCalendarWorkflows &&
					calendarWorkflows.map((calendar: any, index: number) => (
						<CalendarWorkflowComponent
							key={`${calendar.nameOfCalendar}-${calendar.eventTypeId}-${index}`}
							calendar={calendar}
							workflowNumber={workflowCounter++}
							calendarWorkflows={calendarWorkflows}
							onRefetch={handleRefetch}
						/>
					))}
			</div>
		</div>
	);
};
